import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLiff, ScopeGate } from "../../../liff/index.js";
import { fetchIncident, type IncidentData } from "../../../liff/api.js";
import {
  MobileShell, Card, CardHeader, SeverityPill, MobileSpinner, PrimaryButton, SecondaryButton,
} from "../../../liff/components/Mobile.js";

export function IncidentPlaceholderPage(): React.ReactElement {
  const { incidentId } = useParams<{ incidentId: string }>();
  const { session } = useLiff();
  const [data, setData] = useState<IncidentData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"ack" | "resolve" | null>(null);

  useEffect(() => {
    if (!session || !incidentId) return;
    fetchIncident(incidentId, session.session_token)
      .then(setData)
      .catch((e: unknown) => setError((e as Error).message));
  }, [session, incidentId]);

  if (error) return (
    <MobileShell title="無法載入事件"><Card><p className="text-sm text-danger">{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner />;

  const statusLabel = { open: "待處理", ack: "已確認", resolved: "已解決" }[data.status];

  return (
    <MobileShell title={data.title} subtitle={`${data.site_name}${data.location_label ? ` · ${data.location_label}` : ""}`}>
      <Card>
        <div className="flex items-center justify-between mb-3">
          <SeverityPill severity={data.severity} />
          <span className="text-[10px] uppercase tracking-wider text-text-muted">{statusLabel}</span>
        </div>
        <p className="text-sm text-text-primary leading-relaxed whitespace-pre-line">{data.body}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border-subtle text-xs text-text-secondary">
          <span>回報：{data.reporter_name}</span>
          <span>{data.occurred_at_local}</span>
        </div>
      </Card>

      {data.photos.length > 0 && (
        <Card>
          <CardHeader title="現場照片" hint={`${data.photos.length} 張`} />
          <div className="grid grid-cols-2 gap-2">
            {data.photos.map((p) => (
              <a key={p.id} href={p.full_url} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] rounded-lg overflow-hidden bg-bg-raised">
                <img src={p.thumbnail_url} alt={p.caption ?? p.id} loading="lazy" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </Card>
      )}

      {data.checklist_anomalies && data.checklist_anomalies.length > 0 && (
        <Card>
          <CardHeader title="Checklist 異常" hint={`${data.checklist_anomalies.filter((c) => c.status === "fail").length} 項`} />
          <ul className="divide-y divide-border-subtle -my-2">
            {data.checklist_anomalies.map((c, i) => (
              <li key={i} className="py-2 flex items-start gap-2">
                <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${c.status === "ok" ? "bg-success" : "bg-danger"}`} aria-hidden />
                <div className="min-w-0">
                  <p className="text-sm">{c.item}</p>
                  {c.note && <p className="text-[11px] text-text-muted">{c.note}</p>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <CardHeader title="時間線" hint={`${data.timeline.length} 筆`} />
        <ol className="space-y-3">
          {data.timeline.map((t, i) => (
            <li key={i} className="flex gap-3">
              <span className="font-mono text-[11px] text-text-muted shrink-0 mt-0.5 w-12">{t.at_local}</span>
              <div className="min-w-0">
                <p className="text-sm">
                  <span className="font-medium">{t.actor}</span>
                  <span className="text-text-muted"> · {t.action}</span>
                </p>
                {t.note && <p className="text-[11px] text-text-secondary mt-0.5">{t.note}</p>}
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <div className="space-y-2">
        <ScopeGate need="ack:incident" disabled={busy !== null || data.status !== "open"}>
          <PrimaryButton onClick={() => { setBusy("ack"); setTimeout(() => setBusy(null), 600); }}>
            {data.status === "open" ? "確認收到" : "已收到"}
          </PrimaryButton>
        </ScopeGate>
        <ScopeGate need="write:incident">
          <SecondaryButton onClick={() => { setBusy("resolve"); setTimeout(() => setBusy(null), 600); }}>
            標記解決
          </SecondaryButton>
        </ScopeGate>
      </div>
    </MobileShell>
  );
}
